import { useCallback, useEffect, useRef, useState } from 'react';

import { loadCachedPosts, saveCachedPosts } from '@/src/features/posts/feed-cache';
import {
  getPublicPostDetail,
  subscribeToPost,
  type PostComment,
  type PublicPost,
} from './post-service';

type PostState = {
  error: Error | null;
  loading: boolean;
  post: PublicPost | null;
};

export function usePost(
  postId: string | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true;
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const postRef = useRef<PublicPost | null>(null);
  const postIdRef = useRef<string | undefined>(undefined);
  const [state, setState] = useState<PostState>({
    error: null,
    loading: Boolean(postId),
    post: null,
  });

  useEffect(() => {
    const postChanged = postIdRef.current !== postId;

    if (postChanged) {
      postIdRef.current = postId;
      postRef.current = null;
    }

    if (!postId || !enabled) {
      postRef.current = null;
      setRefreshing(false);
      setState({ error: null, loading: false, post: null });
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: postRef.current === null,
      post: postRef.current ?? (postChanged ? null : currentState.post),
    }));

    if (!postRef.current) {
      void loadCachedPosts(`post:${postId}`).then((cachedPosts) => {
        if (cachedPosts[0]) {
          postRef.current = cachedPosts[0];
          setState({ error: null, loading: false, post: cachedPosts[0] });
        }
      });
    }

    return subscribeToPost({
      postId,
      onError: (error) => {
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
        setRefreshing(false);
      },
      onNext: (post) => {
        postRef.current = post;
        setState({ error: null, loading: false, post });
        setRefreshing(false);
        if (post) {
          void saveCachedPosts(`post:${postId}`, [post]);
        }
      },
    });
  }, [enabled, postId, refreshNonce]);

  const refresh = useCallback(() => {
    if (!postId) {
      return;
    }

    setRefreshing(true);
    setRefreshNonce((currentNonce) => currentNonce + 1);
  }, [postId]);

  return {
    ...state,
    refresh,
    refreshing,
  };
}

type PublicPostDetailState = {
  comments: PostComment[];
  error: Error | null;
  loading: boolean;
  post: PublicPost | null;
};

export function usePublicPostDetail(
  postId: string | undefined,
  enabled: boolean,
) {
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [state, setState] = useState<PublicPostDetailState>({
    comments: [],
    error: null,
    loading: Boolean(postId && enabled),
    post: null,
  });

  useEffect(() => {
    let active = true;

    if (!postId || !enabled) {
      setRefreshing(false);
      setState({ comments: [], error: null, loading: false, post: null });
      return () => {
        active = false;
      };
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: currentState.post === null,
    }));

    void getPublicPostDetail(postId)
      .then((detail) => {
        if (!active) {
          return;
        }

        setState({
          comments: detail.comments,
          error: null,
          loading: false,
          post: detail.post,
        });
      })
      .catch((error: Error) => {
        if (!active) {
          return;
        }

        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
      })
      .finally(() => {
        if (active) {
          setRefreshing(false);
        }
      });

    return () => {
      active = false;
    };
  }, [enabled, postId, refreshNonce]);

  const refresh = useCallback(() => {
    if (!postId || !enabled) {
      return;
    }

    setRefreshing(true);
    setRefreshNonce((currentNonce) => currentNonce + 1);
  }, [enabled, postId]);

  return {
    ...state,
    refresh,
    refreshing,
  };
}
